<script setup lang="ts">
import type { HTMLAttributes } from 'vue'

import dayjs from 'dayjs'
import LinePlus from '@docue/docue-ui-v2/icons/LinePlus.vue'
import SolidStar01 from '@docue/docue-ui-v2/icons/SolidStar01.vue'
import LineChevronUp from '@docue/docue-ui-v2/icons/LineChevronUp.vue'
import LineChevronDown from '@docue/docue-ui-v2/icons/LineChevronDown.vue'

import type { PaymentSource } from '~/types/auth'
import type { IntentAmount, PaymentIntentCreatePayload } from '~/composables/api/payment-intent/useFetchPaymentIntent'

import StripeCard from '~/components/StripeCard.vue'

const props = defineProps<{
  disabled?: boolean
  cardClass?: HTMLAttributes['class']
}>()

const { getPaymentIntent: getPaymentIntentHandler } = usePaymentIntent()
const { data, isPending: isLoadingPaymentSources, isFetched } = useQueryPaymentSource.all()
const { t } = useI18n()

const isAddingNewCard = ref(false)
const isOpen = ref(false)
const card = ref<InstanceType<typeof StripeCard>>()

const allPaymentSources = computed(() => data.value?.data ?? [])
const paymentSources = computed(() => allPaymentSources.value.filter(source => source.attributes.type === 'card'))
const selectedPaymentSource = ref<PaymentSource>()

watch(isFetched, () => (isAddingNewCard.value = !paymentSources.value?.length), { immediate: true })
watch(paymentSources, sources => (selectedPaymentSource.value ||= sources?.find(source => source.attributes.role === 'primary')), { immediate: true })

const CardDetails = ({ card }: { card: NonNullable<PaymentSource['attributes']['card']> }) => {
  const { maskedNumber, expiryMonth, expiryYear, brand } = card
  const cardNumber = `${maskedNumber} — (${expiryMonth}/${expiryYear})`

  return h('span', { class: 'inline-flex gap-1.5' }, [
    h('span', { class: 'uppercase' }, brand),
    cardNumber,
  ])
}

const isCardActionsPending = ref(false)
const isDisabled = computed(() => props.disabled || isCardActionsPending.value)

const save = async () => {
  if (!card.value)
    return

  isCardActionsPending.value = true
  try {
    const createdPaymentSource = await card.value.save()
    isOpen.value = false
    isAddingNewCard.value = false

    if (createdPaymentSource)
      selectedPaymentSource.value = createdPaymentSource

    return createdPaymentSource
  }
  catch (e) {
    console.error(e)
    throw e
  }
  finally {
    isCardActionsPending.value = false
  }
}

const getPaymentIntent = async ({ amount }: IntentAmount) => {
  isCardActionsPending.value = true
  if (isAddingNewCard.value)
    return await card.value?.getPaymentIntent({ amount }).finally(() => isCardActionsPending.value = false)

  if (!selectedPaymentSource.value) {
    isCardActionsPending.value = false
    return
  }

  const intentData = {
    amount,
    chargebeeReferenceId: selectedPaymentSource.value.attributes.referenceId,
  } satisfies PaymentIntentCreatePayload

  return await getPaymentIntentHandler(intentData).finally(() => isCardActionsPending.value = false)
}

const now = useNow({ interval: 60_000 })

const selectedPaymentSourceExpiryDate = computed(() => {
  const card = selectedPaymentSource.value?.attributes.card

  if (!card)
    return undefined

  const { expiryMonth, expiryYear } = card

  return dayjs(`${expiryYear}-${expiryMonth}`).endOf('month').toDate()
})

const isValid = computed(() => {
  if (isLoadingPaymentSources.value)
    return true // avoid showing error message while loading

  if (!isAddingNewCard.value)
    return (selectedPaymentSourceExpiryDate.value || 0) > now.value

  if (!card.value)
    return false

  return card.value.isValid ?? true // if isValid is undefined we return true too, as the validation has not been triggered yet
})

const isCardEnterComplete = computed(() => {
  if (!isAddingNewCard.value)
    return true

  return card.value?.isComplete ?? false
})

defineExpose({
  save,
  getPaymentIntent,
  isValid,
  isCardEnterComplete,
})
</script>

<template>
  <div
    v-if="isLoadingPaymentSources"
    class="flex min-h-11 w-full items-center rounded-lg border border-gray-300 px-3.5 py-2.5"
    :class="cardClass"
  >
    <DTLoaderDots />
  </div>

  <template v-else-if="!paymentSources.length || isAddingNewCard">
    <StripeCard
      ref="card"
      :focus-on-ready="isAddingNewCard && !!paymentSources.length"
      :disabled="isDisabled"
      :class="cardClass"
    />
    <div
      v-if="paymentSources.length"
      class="flex gap-1.5"
    >
      <span class="text-xs text-gray-600">
        {{ t('credit-card-input.replaceInfo') }}
      </span>
      <DTButton
        variant="link"
        size="xs"
        dense
        @click="isAddingNewCard = false"
      >
        {{ capitalize(t('credit-card-input.useExistingCard')) }}
      </DTButton>
    </div>
  </template>

  <DTDropdown
    v-else
    @open="isOpen = true"
    @close="isOpen = false"
  >
    <DTDropdownButton
      wrapper-class="w-full [&>button]:w-full"
      class="w-full !justify-between !px-3.5 !py-2.5 !ring-gray-300"
      :disabled="isDisabled"
      :class="cardClass"
    >
      <CardDetails
        v-if="selectedPaymentSource?.attributes.card"
        :card="selectedPaymentSource.attributes.card"
      />
      <component :is="disabled || !isOpen ? LineChevronDown : LineChevronUp" />
    </DTDropdownButton>

    <DTDropdownMenu
      v-if="!disabled"
      content-size="button-width"
    >
      <DTDropdownMenuItem
        v-for="source in paymentSources"
        :key="source.id"
        :disabled="isDisabled"
        @click="selectedPaymentSource = source"
      >
        <CardDetails
          v-if="source.attributes.card"
          :card="source.attributes.card"
        />
        <DTTooltip
          v-if="paymentSources.length > 1 && source.attributes.role === 'primary'"
          :content="capitalize(t('primary'))"
          variant="dark"
          tooltip-class="!z-[100]"
        >
          <SolidStar01
            class="size-4 shrink-0 text-yellow-400"
          />
        </DTTooltip>
      </DTDropdownMenuItem>
      <DTDropdownMenuItem
        :disabled="isDisabled"
        @click="() => {
          isAddingNewCard = true
          isOpen = false
        }"
      >
        <LinePlus class="size-4 text-gray-600" />
        {{ capitalize(t('credit-card-input.useAnotherCard')) }}
      </DTDropdownMenuItem>
    </DTDropdownMenu>
  </DTDropdown>
</template>
